import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../utils/languageUtils';
import RegionSelector from '../RegionSelector';
import './UploadTab.css';

const UploadTab = ({ konvaController, canvasContainerRef }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingFile, setProcessingFile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // Debug long-press trigger refs
  const longPressTimerRef = useRef(null);
  const debugTriggeredRef = useRef(false);

  const [availableRegions, setAvailableRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [hasSelectedImage, setHasSelectedImage] = useState(false);

  // Helper to get localized name
  const getLocalizedName = (nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    const lang = localStorage.getItem('site_language') || 'en';
    return nameObj[lang] || nameObj['en'] || '';
  };
  
  const getCurrentViewConfig = () => {
      const viewId = typeof konvaController?.selectedView === 'string' 
          ? konvaController?.selectedView 
          : konvaController?.selectedView?.id;
      
      // We need configData. Since it's not passed as prop, we might need to access it from konvaController
      const configData = konvaController?.configData;
      
      if (!viewId || !configData?.SupportedModels?.CommonProperties?.Views) return null;
      
      return configData.SupportedModels.CommonProperties.Views.find(v => v.id === viewId);
  };
  
  const currentViewConfig = getCurrentViewConfig();
  const hasClipRegions = currentViewConfig?.ClipRegions && Object.keys(currentViewConfig.ClipRegions).length > 0;

  useEffect(() => {
    if (hasClipRegions) {
      const regions = Object.keys(currentViewConfig.ClipRegions);
      setAvailableRegions(regions);
      // Default to "All" (no clipping) when no selected node
      if (!hasSelectedImage) {
        setSelectedRegions(regions); // represent "All" in UI
      } else {
        // If we have a selected node, sync from it
        const node = konvaController?.getSelectedNode ? konvaController.getSelectedNode() : (typeof window !== 'undefined' ? window.selectedKonvaNode : null);
        if (node) {
          const r = node.getAttr('region');
          if (Array.isArray(r)) {
            setSelectedRegions(r);
          } else if (typeof r === 'string') {
            setSelectedRegions(r.split(',').filter(x => x));
          } else {
            // No regions set on node => show "All" selected
            setSelectedRegions(regions);
          }
        }
      }
    }
  }, [hasClipRegions, currentViewConfig, hasSelectedImage, konvaController]);

  // Two-way sync: reflect selection changes from canvas into UploadTab
  useEffect(() => {
    const onSelectionChanged = (e) => {
      const node = konvaController?.getSelectedNode ? konvaController.getSelectedNode() : (typeof window !== 'undefined' ? window.selectedKonvaNode : null);
      const isImage = !!node && node.className === 'Image';
      setHasSelectedImage(isImage);
      if (isImage && hasClipRegions) {
        const r = node.getAttr('region');
        if (Array.isArray(r)) {
          setSelectedRegions(r);
        } else if (typeof r === 'string') {
          setSelectedRegions(r.split(',').filter(x => x));
        } else {
          setSelectedRegions(availableRegions);
        }
      } else if (hasClipRegions) {
        setSelectedRegions(availableRegions);
      }
    };
    const onNodeRegionUpdated = (e) => {
      const node = e.detail?.node;
      const selectedNode = konvaController?.getSelectedNode ? konvaController.getSelectedNode() : (typeof window !== 'undefined' ? window.selectedKonvaNode : null);
      if (node && selectedNode && node === selectedNode && hasClipRegions) {
        const r = node.getAttr('region');
        if (Array.isArray(r)) {
          setSelectedRegions(r);
        } else if (typeof r === 'string') {
          setSelectedRegions(r.split(',').filter(x => x));
        } else {
          setSelectedRegions(availableRegions);
        }
      }
    };
    window.addEventListener('SELECTION_CHANGED', onSelectionChanged);
    window.addEventListener('NODE_REGION_UPDATED', onNodeRegionUpdated);
    return () => {
      window.removeEventListener('SELECTION_CHANGED', onSelectionChanged);
      window.removeEventListener('NODE_REGION_UPDATED', onNodeRegionUpdated);
    };
  }, [konvaController, hasClipRegions, availableRegions]);

  const toggleRegion = (key) => {
    let next;
    if (selectedRegions.includes(key)) {
      next = selectedRegions.filter(k => k !== key);
    } else {
      next = [...selectedRegions, key];
    }
    setSelectedRegions(next);
    if (konvaController) {
      // Normalize: All selected => []
      const regionsToApply = next.length === availableRegions.length ? [] : next;
      const nodes = typeof konvaController.getSelectedNodes === 'function' ? konvaController.getSelectedNodes() : [];
      if (nodes && nodes.length > 0) {
        try { konvaController.updateSelectedNodes(); } catch {}
        if (typeof konvaController.syncSelectedNodesToRegions === 'function') {
          konvaController.syncSelectedNodesToRegions(regionsToApply);
        }
      } else {
        if (typeof konvaController.setDefaultRegionsForNextAdd === 'function') {
          konvaController.setDefaultRegionsForNextAdd(regionsToApply);
        }
      }
    }
  };

  const toggleAll = () => {
    const all = availableRegions;
    const isAll = selectedRegions.length === all.length;
    const next = isAll ? [] : all;
    setSelectedRegions(next);
    if (konvaController) {
      const regionsToApply = isAll ? [] : all;
      const nodes = typeof konvaController.getSelectedNodes === 'function' ? konvaController.getSelectedNodes() : [];
      if (nodes && nodes.length > 0) {
        try { konvaController.updateSelectedNodes(); } catch {}
        if (typeof konvaController.syncSelectedNodesToRegions === 'function') {
          konvaController.syncSelectedNodesToRegions(regionsToApply);
        }
      } else {
        if (typeof konvaController.setDefaultRegionsForNextAdd === 'function') {
          konvaController.setDefaultRegionsForNextAdd(regionsToApply);
        }
      }
    }
  };

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleImageUpload = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    processFiles(files);
  };

  const processFiles = (files) => {
    setIsLoading(true);
    const fileArray = Array.from(files);
    const newUploadedFiles = [];
    
    // Process files sequentially to avoid overwhelming the canvas
    const processNextFile = (index) => {
      if (index >= fileArray.length) {
        // All files processed
        setUploadedFiles(prev => [...newUploadedFiles, ...prev]);
        setIsLoading(false);
        setProcessingFile(null);
        return;
      }
      
      const file = fileArray[index];
      setProcessingFile(file.name);
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        console.warn(`File ${file.name} exceeds 5MB recommended size`);
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // Add the image to the design group with proper scaling and positioning
          if (!konvaController) {
            console.error('konvaController is not available');
            processNextFile(index + 1);
            return;
          }
          
          konvaController.addImage(e.target.result, {
            name: 'uploaded-image',
            draggable: true,
            // Apply current UI selection to new uploads
            region: (hasClipRegions ? selectedRegions : [])
          }).then(imageObj => {
            if (imageObj) {
              console.log('Image uploaded successfully:', imageObj);
              
              // Get canvas size directly from the stage property
              const stage = konvaController.stage;
              if (!stage) {
                console.error('Stage not available');
                processNextFile(index + 1);
                return;
              }
              const canvas = stage.width();
              
              // Calculate scale to make image 10% of canvas width
              const width = imageObj.width();
              const desiredWidth = canvas * 0.10;
              const scale = desiredWidth / width;
              
              // Apply scaling uniformly to maintain aspect ratio
              imageObj.scaleX(scale);
              imageObj.scaleY(scale);
              
              // Only reposition if NO region was specified (let addImage handle region positioning)
              if (!hasClipRegions || selectedRegions.length === availableRegions.length) {
                  // Detect if we're on mobile
                  const isMobile = window.innerWidth <= 768;
                  
                  if (isMobile) {
                    // On mobile, center the image on the product image
                    const productImagePos = konvaController._productImagePosition;
                    const productImageDim = konvaController._productImageDimensions;
                    
                    if (productImagePos && productImageDim) {
                      // Calculate product image center
                      const prodCenterX = productImagePos.x + (productImageDim.width / 2);
                      const prodCenterY = productImagePos.y + (productImageDim.height / 2);
                      
                      // Center image on product image
                      imageObj.x(prodCenterX - (width * scale / 2));
                      imageObj.y(prodCenterY - (imageObj.height() * scale / 2));
                    } else {
                      // Fallback to canvas center if product image data not available
                      imageObj.x((canvas - width * scale) / 2);
                      imageObj.y((stage.height() - imageObj.height() * scale) / 2);
                    }
                  } else {
                    // On desktop, center the image on canvas
                    imageObj.x((canvas - width * scale) / 2);
                    imageObj.y((stage.height() - imageObj.height() * scale) / 2);
                  }
              } else {
                  // If region was specified, we must adjust position to keep it centered 
                  // in the REGION, considering the new scale.
                  
                  // Get region config again (we have currentViewConfig)
                  const primaryKey = selectedRegions[0];
                  const regionConfig = currentViewConfig?.ClipRegions?.[primaryKey];
                  // Handle potential different structure if any
                  const range = (regionConfig && regionConfig.range) ? regionConfig.range : regionConfig;

                  if (range && Array.isArray(range) && range.length === 2) {
                      let [start, end] = range;
                      
                      // Access internal KonvaController properties for product mapping
                      // Note: these are "private" (_) but we need them for accurate mapping
                      const ratio = konvaController._productImageRatio || 1;
                      const pos = konvaController._productImagePosition || { x: 0, y: 0 };
                      
                      // Handle sentinel for end of image
                      if (end <= 0) {
                          if (konvaController._productImageDimensions && ratio) {
                               end = konvaController._productImageDimensions.height / ratio;
                          } else {
                               end = start + 1000;
                          }
                      }

                      // Calculate center of the region in product space
                      const productCenterY = (start + end) / 2;
                      
                      // Convert to stage space
                      const stageY = (productCenterY * ratio) + pos.y;
                      const stageX = canvas / 2; // Center horizontally
                      
                      // New dimensions
                      const newWidth = width * scale;
                      const newHeight = imageObj.height() * scale;
                      
                      // Position top-left
                      imageObj.x(stageX - newWidth / 2);
                      imageObj.y(stageY - newHeight / 2);
                      
                      console.log(`Region positioning: ${primaryKey} -> StageY: ${stageY}, Pos: ${imageObj.y()}`);
                  } else {
                      // Fallback if config missing: just center on screen
                      imageObj.x((canvas - width * scale) / 2);
                      imageObj.y((stage.height() - imageObj.height() * scale) / 2);
                  }
              }
              
              // Force sync visual node if it exists (for clipped regions)
              if (imageObj.syncVisual) {
                  imageObj.syncVisual();
              }
              
              // Update the layer
              imageObj.getLayer().batchDraw();
              
              // Add to uploaded files list
              newUploadedFiles.push({
                name: file.name,
                type: file.type,
                size: file.size,
                preview: e.target.result,
                timestamp: new Date().getTime()
              });
            }
            
            // Process the next file
            processNextFile(index + 1);
          }).catch(error => {
            console.error('Error adding image:', error);
            processNextFile(index + 1);
          });
        } catch (error) {
          console.error('Error processing image:', error);
          processNextFile(index + 1);
        }
      };
      
      reader.onerror = () => {
        console.error(`Error reading file: ${file.name}`);
        processNextFile(index + 1);
      };
      
      reader.readAsDataURL(file);
    };
    
    // Start processing the first file
    processNextFile(0);
  };

  // --- Fast debug trigger: long press or long tap on upload opens Eruda ---
  const triggerDebugEruda = async () => {
    try {
      // Do NOT persist across reloads; clear any leftover persistence flags
      try {
        if (window.localStorage) {
          window.localStorage.removeItem('app.debug.eruda');
          window.localStorage.removeItem('eruda');
        }
      } catch (_) {}

      // If any previous hard-hide CSS exists, remove it so entry icon can show
      try {
        const tag = document.getElementById('eruda-hidden-style');
        if (tag && tag.parentNode) tag.parentNode.removeChild(tag);
      } catch (_) {}

      // If already loaded, just ensure panel hidden and entry visible
      if (window.eruda) {
        try { if (typeof window.eruda.hide === 'function') window.eruda.hide(); } catch (_) {}
        try {
          if (window.eruda.get) {
            const entry = window.eruda.get('entryButton');
            if (entry && typeof entry.show === 'function') entry.show();
          }
        } catch (_) {}
        console.log('[Eruda] enabled (already loaded) - panel hidden, entry shown');
        return;
      }

      // Lazy-load eruda, init, then hide panel and show entry icon
      const mod = await import('eruda');
      const api = mod.default || mod;
      if (!api) return;
      try { if (typeof api.init === 'function') api.init(); } catch (_) {}
      try { if (typeof api.hide === 'function') api.hide(); } catch (_) {}
      try {
        if (api.get) {
          const entry = api.get('entryButton');
          if (entry && typeof entry.show === 'function') entry.show();
        }
      } catch (_) {}
      console.log('[Eruda] enabled via long-press - panel hidden, entry shown');
    } catch (e) {
      console.warn('[Eruda] enable failed:', e);
    }
  };

  const startLongPress = () => {
    if (isLoading) return;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      debugTriggeredRef.current = true;
      triggerDebugEruda();
      // auto-clear flag shortly after to avoid blocking later clicks
      setTimeout(() => { debugTriggeredRef.current = false; }, 800);
    }, 800); // 800ms press to trigger
  };

  const cancelLongPress = () => {
    clearTimeout(longPressTimerRef.current);
  };

  const handleUploadClick = (e) => {
    if (debugTriggeredRef.current) {
      // Swallow the click that follows a long-press
      e && e.preventDefault();
      debugTriggeredRef.current = false;
      return;
    }
    handleClick();
  };


  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  const removeFile = (timestamp) => {
    setUploadedFiles(prev => prev.filter(file => file.timestamp !== timestamp));
  };

  return (
    <div className="upload-tab">
      {hasClipRegions && (
        <div style={{ padding: '10px 15px', background: '#f5f5f5', marginBottom: '10px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('common.region') || 'Region'}:</div>
          <RegionSelector
            value={selectedRegions}
            onChange={(newRegions) => {
              console.log('[UploadTab] STEP 1: Region selection changed', {
                oldRegions: selectedRegions,
                newRegions: newRegions,
                availableRegions: availableRegions
              });
              setSelectedRegions(newRegions);
              
              // Auto-sync selected nodes to new regions
              if (konvaController) {
                const nodes = typeof konvaController.getSelectedNodes === 'function' ? konvaController.getSelectedNodes() : [];
                if (nodes && nodes.length > 0) {
                  console.log('[UploadTab] STEP 2: Calling updateSelectedNodes');
                  konvaController.updateSelectedNodes();
                  console.log('[UploadTab] STEP 3: Calling syncSelectedNodesToRegions', newRegions);
                  konvaController.syncSelectedNodesToRegions(newRegions);
                  console.log('[UploadTab] STEP 4: Region sync completed');
                } else {
                  if (typeof konvaController.setDefaultRegionsForNextAdd === 'function') {
                    console.log('[UploadTab] No selection, set default regions for next add:', newRegions);
                    konvaController.setDefaultRegionsForNextAdd(newRegions);
                  }
                }
              } else {
                console.log('[UploadTab] ERROR: konvaController not available');
              }
            }}
            multiSelect={true}
            options={availableRegions.map(regionKey => {
              const regionConfig = currentViewConfig.ClipRegions[regionKey];
              const displayName = regionConfig.displayName ? getLocalizedName(regionConfig.displayName) : regionKey;
              return { label: displayName, value: regionKey };
            })}
          />
        </div>
      )}

      {isMobile ? (
        // Mobile UI - Compact design
        <div className="upload-mobile">
          {isLoading ? (
            <div className="upload-loading-mobile">
              <div className="spinner-ring-small"></div>
              <span>{t('upload.uploading', 'Uploading...')}</span>
            </div>
          ) : (
            <button 
              className="upload-button-mobile"
              onMouseDown={startLongPress}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={startLongPress}
              onTouchEnd={cancelLongPress}
              onClick={handleUploadClick}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {t('upload.selectFiles', 'Select Files')}
            </button>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
            multiple
            disabled={isLoading}
            style={{ display: 'none' }}
          />
        </div>
      ) : (
        // Desktop UI - Full dropzone
        <div 
          className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${isLoading ? 'loading' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onMouseDown={startLongPress}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onClick={isLoading ? null : handleUploadClick}
        >
          {isLoading ? (
            <div className="upload-loading">
              <div className="loading-spinner">
                <div className="spinner-ring"></div>
              </div>
              <div className="loading-text">
                <h4>{t('upload.processing', 'Processing')}</h4>
                <p>{processingFile || t('upload.uploading', 'Uploading...')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="upload-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </div>
              <div className="upload-content">
                <h4 className="upload-main-text">{t('upload.dragDrop', 'Drag and drop files here')}</h4>
                <p className="upload-secondary-text">{t('upload.clickBrowse', 'or click to browse')}</p>
                <div className="upload-button">
                  <span>{t('upload.selectFiles', 'Select Files')}</span>
                </div>
              </div>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
            multiple
            disabled={isLoading}
            style={{ display: 'none' }}
          />
        </div>
      )}

      <div className="upload-tip">
        <span className="tip-icon" aria-hidden="true">ℹ</span>
        <p>{t('upload.recommendedSize', 'Recommended: under 5MB per file for best performance')}</p>
      </div>
      <div className="upload-tip">
        <span className="tip-icon" aria-hidden="true">ℹ</span>
        <p>{t('upload.recommendedFormats', 'Recommended formats: PNG, JPG, WEBP, SVG')}</p>
      </div>
      
      {uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <h4>{t('upload.recentlyUploaded', 'Recently Uploaded')}</h4>
          <div className="file-grid">
            {uploadedFiles.slice(0, 6).map((file) => (
              <div className="file-item" key={file.timestamp}>
                <div 
                  className="file-preview"
                  onClick={() => {
                    // Add the image to the design again when clicked
                    if (konvaController && !isLoading) {
                      setIsLoading(true);
                      console.log('[UploadTab] Adding image with regions:', {
                        selectedRegions,
                        hasRegions: selectedRegions.length > 0,
                        regionParam: selectedRegions.length > 0 ? selectedRegions : undefined
                      });
                      konvaController.addImage(file.preview, {
                        name: 'uploaded-image',
                        draggable: true,
                        region: (hasClipRegions ? selectedRegions : [])
                      }).then(imageObj => {
                        if (imageObj) {
                          // Apply the same scaling and positioning as in processFiles
                          const stage = konvaController.stage;
                          if (stage) {
                            const canvas = stage.width();
                            const width = imageObj.width();
                            const desiredWidth = canvas * 0.10;
                            const scale = desiredWidth / width;
                            
                            imageObj.scaleX(scale);
                            imageObj.scaleY(scale);
                            
                            const isMobile = window.innerWidth <= 768;
                            
                            if (isMobile && konvaController._productImagePosition && konvaController._productImageDimensions) {
                              const prodPos = konvaController._productImagePosition;
                              const prodDim = konvaController._productImageDimensions;
                              const prodCenterX = prodPos.x + (prodDim.width / 2);
                              const prodCenterY = prodPos.y + (prodDim.height / 2);
                              
                              imageObj.x(prodCenterX - (width * scale / 2));
                              imageObj.y(prodCenterY - (imageObj.height() * scale / 2));
                            } else {
                              imageObj.x((canvas - width * scale) / 2);
                              imageObj.y((stage.height() - imageObj.height() * scale) / 2);
                            }
                            
                            imageObj.getLayer().batchDraw();
                          }
                        }
                        setIsLoading(false);
                      }).catch(() => setIsLoading(false));
                    }
                  }}
                >
                  <img src={file.preview} alt={file.name} />
                </div>
                <div className="file-info">
                  <p className="file-name" title={file.name}>
                    {file.name.length > 15 ? `${file.name.substring(0, 12)}...` : file.name}
                  </p>
                  <button 
                    className="file-remove" 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.timestamp);
                    }}
                    title="Remove from history"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadTab;

